import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { ALL_ROUTES } from '../../../core/router/routes';
import DarkModeToggle from 'front/components/ui/DarkModeToggle';
import { useTheme } from '../../../core/contexts/ThemeContext';
import Button from '../../components/ui/Button';
import { getUniversities, getDepartments, University, Department } from '../../services/commonService';
import { useFrontAuthStore } from '../../auth/store/authStore';

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-height: 100vh;
  background-color: var(--bg-primary);
  padding: 16px;
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    padding: 8px;
  }
`;

const IntroSection = styled.section`
  position: relative;
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: calc(100vh - 76px);
  padding: 0 44px;
  border: 1px solid ${props => props.theme.colors.book.color3b};
  background: #fff url('/images/global/hero_bg.jpg') no-repeat center center;
  background-size: cover;
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    padding: 0 24px;
  }
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    padding: 0 16px;
    height: auto;
  }
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 22px 0;
  border-bottom: 1px solid ${props => props.theme.colors.book.color3b};
`;

const Logo = styled.h1`
  margin: 0;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
`;

const HeaderButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.xl};
  border-radius: ${props => props.theme.borderRadius.lg};
  font-weight: ${props => props.theme.fontWeight.semibold};
  font-size: ${props => props.theme.fontSize.base};
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.md};
    font-size: ${props => props.theme.fontSize.sm};
  }
`;

const HeroSection = styled.section`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 30px;
  padding: clamp(${props => props.theme.spacing['3xl']}, 4.69vw, ${props => props.theme.spacing['5xl']}) clamp(1px, 3.13vw, ${props => props.theme.spacing['3xl']});
  text-align: center;
  height: calc(100vh - 160px);
  width: 100%;
  max-width: 900px;
  letter-spacing: -0.02em;
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    height: auto;
    padding: ${props => props.theme.spacing['3xl']} 0;
  }
`;

const HeroTitle = styled.h2`
  font-size: clamp(${props => props.theme.fontSize.xl}, 4.88vw, ${props => props.theme.fontSize['5xl-md']});
  font-weight: ${props => props.theme.fontWeight.bold};
  color: #0b0b0b;
  line-height: 1.4;
  & strong {
    color: #6AAC00;
    font-weight: ${props => props.theme.fontWeight.bold};
  }
`;

const HeroSubtitle = styled.p`
  font-size: clamp(${props => props.theme.fontSize.lg}, 2.34vw, ${props => props.theme.fontSize['2xl']});
  color: #3b3b3b;
  margin-bottom: ${props => props.theme.spacing['4xl']};
`;

const InputGroup = styled.div`
  position: relative;
  z-index: 10;
  display: flex;
  gap: 10px;
  width: 100%;
  flex-wrap: wrap;
  justify-content: center;
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    flex-direction: column;
  }
`;

const SelectWrapper = styled.div`
  position: relative;
  flex: 1;
  max-width: 360px;
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    max-width: 100%;
  }
`;

const SearchableSelectContainer = styled.div<{ $isOpen: boolean; $disabled: boolean }>`
  position: relative;
  z-index: ${props => props.$isOpen ? 1100 : 1};
  width: 100%;
  border-radius: ${props => props.theme.borderRadius.lg};
  background-color: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.7);
  ${props => props.$disabled && `
    opacity: 0.8;
    cursor: not-allowed;
  `}
`;

const SelectInput = styled.input<{ $isOpen: boolean }>`
  width: 100%;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing['5xl']} ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
  font-size: ${props => props.theme.fontSize.base};
  transition: all 0.2s ease;
  height: 5rem;
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    height: 4rem;
    font-size: ${props => props.theme.fontSize.sm};
  }
  cursor: pointer;
  background-color: transparent;
  &::placeholder {
    color: #2b2b2b;
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.toss.blue};
    box-shadow: 0 0 0 4px ${props => props.theme.colors.toss.blue}20;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const SelectArrow = styled.span`
  position: absolute;
  right: ${props => props.theme.spacing.lg};
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #2b2b2b;
  font-size: ${props => props.theme.fontSize.sm};
`;

const DropdownList = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + ${props => props.theme.spacing.xs});
  left: 0;
  right: 0;
  background-color: #ffffff;
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows.lg};
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  display: ${props => props.$isOpen ? 'block' : 'none'};
`;

const SearchInput = styled.input`
  width: 100%;
  height: 4rem;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border: none;
  border-bottom: 1px solid #e5e5e5;
  border-radius: ${props => props.theme.borderRadius.xl} ${props => props.theme.borderRadius.xl} 0 0;
  font-size: ${props => props.theme.fontSize.base};
  font-family: inherit;
  background-color: #f5f5f5;

  &:focus {
    outline: none;
    background-color: #ffffff;
  }

  &::placeholder {
    color: #2b2b2b;
  }
`;

const DropdownItem = styled.div<{ $isSelected: boolean }>`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  cursor: pointer;
  transition: background-color 0.15s ease;
  background-color: ${props => props.$isSelected ? '#2b2b2b' : '#ffffff'};
  text-align: left;
  color: ${props => props.$isSelected ? '#fff' : '#2b2b2b'};
  &:hover {
    background-color: #f5f5f5;
  }
`;

const EmptyMessage = styled.div`
  padding: ${props => props.theme.spacing.lg};
  text-align: center;
  color: #2b2b2b;
  font-size: ${props => props.theme.fontSize.sm};
`;

interface SearchableSelectProps<T> {
  options: T[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
  getOptionValue: (option: T) => string;
  getOptionLabel: (option: T) => string;
}

const SearchableSelect = <T,>({ 
  options, 
  value, 
  onChange, 
  placeholder, 
  disabled = false,
  getOptionValue,
  getOptionLabel
}: SearchableSelectProps<T>) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const containerRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const selectedOption = options.find(opt => getOptionValue(opt) === value);
  const displayValue = selectedOption ? getOptionLabel(selectedOption) : '';

  const filteredOptions = options.filter(opt =>
    getOptionLabel(opt).toLowerCase().includes(searchTerm.toLowerCase())
  );

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (option: T) => {
    onChange(getOptionValue(option));
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  return (
    <SearchableSelectContainer ref={containerRef} $isOpen={isOpen} $disabled={disabled}>
      <SelectInput
        type="text"
        value={isOpen ? searchTerm : displayValue}
        onChange={handleInputChange}
        onClick={handleInputClick}
        placeholder={placeholder}
        disabled={disabled}
        $isOpen={isOpen}
        readOnly={!isOpen}
      />
      <SelectArrow><img src="/images/global/icon_arrow_down.svg" alt="select_arrow" /></SelectArrow>
      <DropdownList $isOpen={isOpen}>
        <SearchInput
          ref={searchInputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="검색..."
          onClick={(e) => e.stopPropagation()}
        />
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => (
            <DropdownItem
              key={getOptionValue(option)}
              $isSelected={getOptionValue(option) === value}
              onClick={() => handleSelect(option)}
            >
              {getOptionLabel(option)}
            </DropdownItem>
          ))
        ) : (
          <EmptyMessage>검색 결과가 없습니다.</EmptyMessage>
        )}
      </DropdownList>
    </SearchableSelectContainer>
  );
};

const EnterButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff !important;
  width: 160px;
  height: 5rem;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
  font-size: ${props => props.theme.fontSize.lg};
  border-radius: ${props => props.theme.borderRadius.lg};
  background: linear-gradient(105deg, #082E27 11.83%, #093D33 86.87%);
  transition: all 0.2s ease;
  &:hover {
    background: linear-gradient(105deg, #082E27 11.83%, #093D33 86.87%);
  }
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    width: 100%;
    height: 4rem;
  }
`;

const PersonalLearnerLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  color: #6b6b6b;
  text-decoration: none;
  font-size: ${props => props.theme.fontSize.base};
  margin-top: ${props => props.theme.spacing.md};
  transition: color 0.2s ease;

  &:hover {
    color: ${props => props.theme.colors.toss.blue};
  }
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.fontSize.sm};
  }
`;

const marqueeScroll = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const MessageSection = styled.section`
  display: flex;
  align-items: center;
  width: 100%;
  height: 60px;
  border: 1px solid ${props => props.theme.colors.book.color3b};
  border-width: 1px 0;
  background-color: var(--message-bg);
  overflow: hidden;
`;

const MarqueeTrack = styled.div`
  display: flex;
  white-space: nowrap;
  animation: ${marqueeScroll} 150s linear infinite;
`;

const MessageTitle = styled.h3`
  flex-shrink: 0;
  padding: 0 40px;
  font-size: ${props => props.theme.fontSize['sm']};
  font-weight: ${props => props.theme.fontWeight.normal};
  color: ${props => props.theme.colors.gray[900]};
  letter-spacing: 0.06em;
`;


const TemplateHeader = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  gap: ${props => props.theme.spacing['2xl']};
`;

const TemplateSubTitle = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  & strong {
    color: ${props => props.theme.colors.book.color0b};
    font-family: "Roboto Mono";
    font-size: ${props => props.theme.fontSize.sm};
    font-weight: ${props => props.theme.fontWeight.normal};
    text-transform: uppercase;
  }
  html.dark-mode & img {
    filter: invert(1);
  }
`;

const TemplateTitle = styled.h3`
  font-size: clamp(${props => props.theme.fontSize.xl}, 4.88vw, ${props => props.theme.fontSize['5xl-md']});
  font-weight: ${props => props.theme.fontWeight.medium};
  color: ${props => props.theme.colors.book.color0b};
  text-align: center;
  letter-spacing: -0.04em;
  line-height: 140%;
`;

const BookCarouselSection = styled.section`
  overflow: hidden;
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  padding: 80px ${props => props.theme.spacing.xl};
  gap: 85px;
  width: 100%;
  max-width: 1440px;
  height: 100vh;
  box-sizing: border-box;
  background-color:  var(--bg-primary);
  clip-path: inset(0 -100vw);
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 280px;
    background: linear-gradient(180deg, rgba(244, 245, 246, 0.00) 4.7%, var(--bg-primary) 86.93%);
    pointer-events: none;
    z-index: 2;
  }
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    flex-direction: column;
    gap: 0;
    padding: 0 ${props => props.theme.spacing.xl};
  }
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    padding: 0 ${props => props.theme.spacing.lg};
  }
`;

const BookHeader = styled.div`
  width: 600px;
  flex-shrink: 0;
  font-size: ${props => props.theme.fontSize['3xl']};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.book.color1b};
  & strong {
    color: ${props => props.theme.colors.book.color2b};
    font-family: "Roboto Mono";
    font-size: ${props => props.theme.fontSize.sm};
    font-weight: ${props => props.theme.fontWeight.normal};
    text-transform: uppercase;
  }
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.xl};
    background-color: var(--bg-primary);
    z-index: 5;
  }
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    padding: ${props => props.theme.spacing.lg};
  }
`;

const BookHeaderTitle = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  & span {
    color: ${props => props.theme.colors.book.color2b};
    font-family: "Roboto Mono";
    font-size: ${props => props.theme.fontSize.sm};
    font-weight: ${props => props.theme.fontWeight.normal};
  }
  html.dark-mode & img {
    filter: invert(1);
  }
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    display: none;
  }
`;

const BookHeaderDescription = styled.h3`
  padding: ${props => props.theme.spacing.xl} 0 clamp(${props => props.theme.spacing['3xl']}, 5.86vw, 60px);
  color: ${props => props.theme.colors.book.color0b};
  font-size: clamp(${props => props.theme.fontSize.xl}, 4.88vw, ${props => props.theme.fontSize['5xl-md']});
  font-weight: ${props => props.theme.fontWeight.medium};
  letter-spacing: -0.075em;
  line-height: 140%;
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    display: none;
  }
`;
const UniversitySelect = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${props => props.theme.spacing.md};
  & button {
    display: flex;
    align-items: center;
    height: 66px;
    padding: 0 ${props => props.theme.spacing.lg};
    border-radius: ${props => props.theme.borderRadius.lg};
    background-color: ${props => props.theme.colors.white};
    color: ${props => props.theme.colors.book.color2b};
    font-size: clamp(${props => props.theme.fontSize.base}, 1.95vw, ${props => props.theme.fontSize.xl});
    font-weight: ${props => props.theme.fontWeight.medium};
    &.active {
      border: none;
      outline: none;
      color: #fff;
      background: url('/images/main/icon_select_arrow.svg') no-repeat calc(100% - 16px) center, linear-gradient(105deg, #082E27 11.83%, #093D33 86.87%);
    }
    &:active {
      outline: none;
      border: none;
    }
  }
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    flex-flow: row wrap;
    & button {
      height: 52px;
      font-size: ${props => props.theme.fontSize.base};
      &.active {
        padding-right: ${props => props.theme.spacing['5xl']};
      }
    }
  }
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    & button {
      height: 48px;
      font-size: ${props => props.theme.fontSize.sm};
      &.active {
        padding-right: ${props => props.theme.spacing['4xl']};
      }
    }
  }
`;

const CarouselContainer = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-flow: column nowrap;
  gap: ${props => props.theme.spacing.lg};
  height: 100%;
  overflow: hidden;
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    min-height: 0;
    padding-top: 80px;
  }
`;

const CarouselInner = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: 60px;
  margin-bottom: ${props => props.theme.spacing['3xl']};
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    gap: ${props => props.theme.spacing['3xl']};
  }
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    gap: ${props => props.theme.spacing.xl};
  }
`;

const BookCard = styled.div`
  overflow: hidden;
  width: calc(50% - 30px);
  max-width: 350px;
  min-height: 386px;
  border-radius: 0 0 ${props => props.theme.borderRadius.lg} ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.book.color2b};
  flex-shrink: 0;
  &:nth-child(even) {
    transform: translateY(150px);
  }
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    width: calc(50% - 16px);
    max-width: none;
    min-height: 320px;
    &:nth-child(even) {
      transform: translateY(100px);
    }
  }
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    width: calc(50% - 10px);
    min-height: 280px;
    &:nth-child(even) {
      transform: translateY(80px);
    }
  }
`;

const BookCover = styled.div`
  width: 100%;
  aspect-ratio: 3/4;
  padding: ${props => props.theme.spacing['3xl']};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: ${props => props.theme.spacing.md};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.gray[600]};
  font-size: ${props => props.theme.fontSize.sm};
`;

const BookTitle = styled.h4`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.lg};
  gap: ${props => props.theme.spacing.md};
  font-weight: ${props => props.theme.fontWeight.normal};
  color: #2b2b2b;
  letter-spacing: -0.02em;
  border-bottom: 1px solid ${props => props.theme.colors.book.color2b};
  background: linear-gradient(92deg, #DDD0FF 0%, #E6EAD8 32.42%, #D3FFD8 66.78%, #CADCFF 100.65%), #F0F0F0;
  background-blend-mode: lighten, normal;
`;

const FeaturesSection = styled.section`
  display: flex;
  flex-flow: column nowrap;
  gap: 80px;
  padding: 140px 0;
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    gap: ${props => props.theme.spacing['5xl']};
    padding: ${props => props.theme.spacing['6xl']} 0;
    width: 100%;
  }
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    gap: ${props => props.theme.spacing['3xl']};
    padding: ${props => props.theme.spacing['5xl']} 0;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
  gap: ${props => props.theme.spacing.xl};
  max-width: 1440px;
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    width: 100%;
  }
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  overflow: hidden;
  position: relative;
  max-width: 460px;
  min-height: 620px;
  background-color: #DBDBDB;
  border-radius: ${props => props.theme.borderRadius.lg};
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    max-width: 100%;
    min-height: 420px;
  }
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    width: 100%;
    min-height: auto;
    height: 300px;
  }
`;

const FeatureCover = styled.div`
  width: 100%;
  height: 100%;
  border-radius: ${props => props.theme.borderRadius.md};
  overflow: hidden;
  & img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const FeatureContent = styled.div`
  position: absolute;
  left: 10px;
  right: 10px;
  bottom: 10px;  
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  justify-content: center;
  flex-flow: column nowrap;
  padding: ${props => props.theme.spacing['xl']};
  border-radius: ${props => props.theme.borderRadius.md};
  background: rgba(0, 0, 0, 0.30);
  gap: ${props => props.theme.spacing.md};
`;

const FeatureTitle = styled.h4`
  margin: 0;
  color: #fff;
  font-size: ${props => props.theme.fontSize['2xl']};
  font-weight: ${props => props.theme.fontWeight.bold};
  line-height: 100%;
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.fontSize.lg};
  }
`;

const FeatureDescription = styled.p`
  font-size: ${props => props.theme.fontSize.lg};
  font-weight: ${props => props.theme.fontWeight.normal};
  color: #fff;
  line-height: 140%;
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.fontSize.sm};
  }
`;

/* price section */
const PriceSection = styled.section`
  display: flex;
  flex-flow: column nowrap;
  gap: clamp(${props => props.theme.spacing['3xl']}, 7.81vw, 80px);
  padding-bottom: clamp(${props => props.theme.spacing['8xl']}, 13.67vw, 140px);
  width: 100%;
  max-width: 1440px;
`;

const PriceContent = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  padding: clamp(${props => props.theme.spacing['5xl']}, 7.81vw, 80px) clamp(${props => props.theme.spacing['4xl']}, 9.28vw, 95px) clamp(${props => props.theme.spacing['6xl']}, 11.72vw, 120px);
  gap: clamp(${props => props.theme.spacing['3xl']}, 5.86vw, 60px);
  width: 100%;
  border-radius: ${props => props.theme.borderRadius.lg};
  background: linear-gradient(105deg, #082E27 11.83%, #093D33 86.87%);
  & h4 {
    color: #fff;
    font-size: ${props => props.theme.fontSize['3xl']};
    font-weight: ${props => props.theme.fontWeight.bold};
    line-height: 100%;
  }
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    & h4 {
      font-size: ${props => props.theme.fontSize.xl};
    }
  }
`;

const PriceList = styled.ul`
  display: flex;
  flex-flow: column nowrap;
  gap: ${props => props.theme.spacing.xl};
  width: 100%;
`;

const PriceItem = styled.li`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  height: 64px;
  font-size: ${props => props.theme.fontSize.xl};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: #fff;
  border-radius: 500px;
  background: rgba(0, 0, 0, 0.10) url('/images/main/icon_price_arrow.svg') no-repeat 22px center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: inset 0 1px 1px 0 rgba(255, 255, 255, 0.2);
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    height: 48px;
    font-size: ${props => props.theme.fontSize.base};
  }
`;

/* FAQ section */
const FAQSection = styled.section`
  display: flex;
  flex-flow: column nowrap;
  gap: 80px;
  padding-bottom: clamp(${props => props.theme.spacing['8xl']}, 13.67vw, 140px);
  width: 100%;
  max-width: 1440px;
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    gap: ${props => props.theme.spacing['5xl']};
  }
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    gap: ${props => props.theme.spacing['3xl']};
  }
`;

const FAQList = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${props => props.theme.spacing.lg};
  width: 100%;
`;

const FAQItem = styled.div`
  overflow: hidden;
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  border-radius: ${props => props.theme.borderRadius['2xl']};
  background-color: ${props => props.theme.colors.white};
`;

const FAQQuestion = styled.button<{ $isOpen: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing['3xl']} ${props => props.theme.spacing['2xl']};
  text-align: left;
  font-size: clamp(${props => props.theme.fontSize.base}, 2.15vw, ${props => props.theme.fontSize['xl-lg']});
  font-weight: ${props => props.theme.fontWeight.normal};
  line-height: 130%;
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.lg};
  }
  color: ${props => props.$isOpen ? '#fff' : props.theme.colors.book.color2b};
  border: none;
  cursor: pointer;
  outline: none;
  background: ${props => props.$isOpen ? 'linear-gradient(105deg, #082E27 11.83%, #093D33 86.87%)' : 'transparent'};
  transition: color, background 0.3s ease;
  &:active {
    outline: none;
    border: none;
  }
`;

const FAQIcon = styled.span<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform, background 0.2s ease;
  background: ${props => props.$isOpen ? props.theme.colors.white : '#202527'};
  border-radius: ${props => props.theme.borderRadius['2xl']};
  width: 40px;
  height: 32px;
  padding: 4px;
  & svg path:last-child {
    transform: ${props => props.$isOpen ? 'rotate(90deg)' : 'rotate(0deg)'};
    fill: ${props => props.$isOpen ? props.theme.colors.book.color2b : '#fff'};
    transform-origin: center;
    transition: transform 0.3s ease;
  }
`;

const FAQAnswer = styled.div<{ $isOpen: boolean }>`
  padding: ${props => props.$isOpen ? props.theme.spacing['3xl'] : '0'} ${props => props.theme.spacing['2xl']};
  max-height: ${props => props.$isOpen ? '500px' : '0'};
  font-size: clamp(${props => props.theme.fontSize.base}, 2.15vw, ${props => props.theme.fontSize['xl-lg']});
  font-weight: ${props => props.theme.fontWeight.normal};
  line-height: 150%;
  color: ${props => props.theme.colors.book.color2b};
  overflow: hidden;
  transition: max-height 0.3s ease;
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    padding: ${props => props.$isOpen ? props.theme.spacing.xl : '0'} ${props => props.theme.spacing.lg};
  }
`;

/* footer section */
const Footer = styled.footer`
  display: flex;
  flex-flow: column nowrap;
  gap: ${props => props.theme.spacing['6xl']};
  width: 100%;
  padding: ${props => props.theme.spacing['8xl']} ${props => props.theme.spacing['5xl']};
  background: linear-gradient(105deg, #05211C 11.83%, #093D33 86.87%);
  border-radius: ${props => props.theme.borderRadius.lg};
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    padding: ${props => props.theme.spacing['5xl']} ${props => props.theme.spacing['3xl']};
    gap: ${props => props.theme.spacing['5xl']};
  }
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    padding: ${props => props.theme.spacing['3xl']} ${props => props.theme.spacing.xl};
  }
`;

const FooterTitle = styled.h2`
  padding-bottom: ${props => props.theme.spacing['8xl']};
  font-size: clamp(${props => props.theme.fontSize.lg}, 4.88vw, ${props => props.theme.fontSize['5xl-md']});
  font-weight: ${props => props.theme.fontWeight.medium};
  line-height: 150%;
  letter-spacing: -0.02em;
  color: #fff;
  text-align: center;
  border-bottom: 1px solid #CBCBCB;
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    padding-bottom: ${props => props.theme.spacing['5xl']};
  }
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    padding-bottom: ${props => props.theme.spacing['3xl']};
  }
`;

const FooterContent = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${props => props.theme.spacing['5xl']};
  width: 100%;
`;

const FooterHeader = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  & h2 {
    filter: brightness(0) invert(1);
  }
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    flex-flow: column nowrap;
    align-items: flex-start;
    gap: ${props => props.theme.spacing.xl};
  }
`;

const FamilySiteWrapper = styled.div`
  position: relative;
  height: 54px;
  min-width: 250px;
  margin-right: 176px;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    margin-right: 0;
  }
`;

const FamilySiteButton = styled.button<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${props => props.theme.spacing.sm};
  width: 100%;
  height: 54px;
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.xl};
  border-radius: ${props => props.theme.borderRadius.lg};
  background: #fff;
  color: #2b2b2b;
  font-size: ${props => props.theme.fontSize.lg};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.6);
  }

  & svg {
    transition: transform 0.2s ease;
    transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
`;

const FamilySiteMenu = styled.ul<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + ${props => props.theme.spacing.sm});
  right: 0;
  min-width: 250px;
  background: #fff;
  border-radius: ${props => props.theme.borderRadius.md};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(8px)'};
  transition: all 0.2s ease;
  z-index: 100;

  & li {
    list-style: none;
  }

  & a {
    display: block;
    padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
    color: #4b4b4b;
    text-decoration: none;
    font-size: ${props => props.theme.fontSize.sm};
    transition: background 0.15s ease;

    &:hover {
      background: #f4f4f4;
    }
  }
`;

const FooterInfo = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${props => props.theme.spacing['3xl']};
  padding-bottom: clamp(${props => props.theme.spacing.lg}, 3.13vw, ${props => props.theme.spacing['3xl']});
  & > dl {
    display: flex;
    flex-flow: row nowrap;
    gap: ${props => props.theme.spacing['5xl']};
    & dt {
      color: #DBDBDB;
      font-size: ${props => props.theme.fontSize.lg};
      font-weight: ${props => props.theme.fontWeight.bold};
      letter-spacing: -0.04em;
    }
    & dd {
      display: flex;
      flex-flow: column nowrap;
      gap: 0;
      & p {
        display: flex;
        flex-flow: row nowrap;
        gap: ${props => props.theme.spacing.xl};
        font-size: ${props => props.theme.fontSize.lg};
        letter-spacing: -0.04em;
        & strong {
          width: 50px;
          color: #DBDBDB;
          font-weight: ${props => props.theme.fontWeight.medium};
        }
        & a, span {
          color: #CBCBCB;
          font-weight: ${props => props.theme.fontWeight.light};
        }
        & a {
          text-decoration: underline;
        } 
      }
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    & > dl {
      gap: ${props => props.theme.spacing.xl};
      & dt {
        font-size: ${props => props.theme.fontSize.base};
      }
      & dd {
        & p {
          gap: ${props => props.theme.spacing.lg};
          font-size: ${props => props.theme.fontSize.sm};
        }
      }
        & p {
          gap: ${props => props.theme.spacing.lg};
          font-size: ${props => props.theme.fontSize.sm};
        }
      }
    }
  }
`;

const FooterInfoItem = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: ${props => props.theme.spacing.sm};
  max-width: 1000px;
  color: #EBEBEB;
  font-size: ${props => props.theme.fontSize.lg};
  line-height: 166%;
  letter-spacing: -0.04em;
  & strong {
    display: inline-block;  
    font-weight: ${props => props.theme.fontWeight.bold};
  }
  & span {
    position: relative;
    display: inline-block;
    color: #DBDBDB;
    font-weight: ${props => props.theme.fontWeight.light};
    padding-left: ${props => props.theme.spacing.sm};
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 1px;
      height: 12px;
      background: #DBDBDB;
    }
    &:first-child::before {
      display: none;
    }
  }
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.fontSize.base};
    & strong {
      font-size: ${props => props.theme.fontSize.base};
    }
    & span {
      font-size: ${props => props.theme.fontSize.sm};
    }
  }
`;

const FooterCopyright = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  border-top: 1px solid #CBCBCB;
  padding-top: ${props => props.theme.spacing['5xl']};
  & address {
    font-family: "Roboto Mono", monospace;
    color: #DBDBDB;
    font-size: ${props => props.theme.fontSize.lg};
    font-weight: ${props => props.theme.fontWeight.medium};
    line-height: 150%;
    font-style: normal;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    flex-flow: column nowrap;
    align-items: flex-start;
    gap: ${props => props.theme.spacing.xl};
    padding-top: ${props => props.theme.spacing['3xl']};
    & address {
      font-size: ${props => props.theme.fontSize.sm};
    }
  }
`;

const FooterNav = styled.ul`
  display: none;
  flex-flow: row nowrap;
  gap: ${props => props.theme.spacing.lg};
  & li {
    position: relative;
    list-style: none;
    padding-left: ${props => props.theme.spacing.lg};
    line-height: 150%;
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 1px;
      height: 16px;
      background: #CECECE;
    }
    &:first-child::before {
      display: none;
    }
  }
  & a {
    color: #DBDBDB;
    font-size: ${props => props.theme.fontSize.lg};
    font-weight: ${props => props.theme.fontWeight.medium};
    line-height: 150%;
  }
`;


const universityTabs = [
  { key: 'jeju', label: '제주대학교 간호학과 커리큘럼 도서' },
  { key: 'seoul', label: '서울대학교 간호학과 커리큘럼 도서' },
  { key: 'kyunghee', label: '경희대학교 간호학과 커리큘럼 도서' },
  { key: 'gunkuk', label: '건국대학교 간호학과 커리큘럼 도서' },
];

const booksByUniversity: Record<string, { title: string; thumbUrl: string }[]> = {
  jeju: [
    { title: '심리학의 세계', thumbUrl: '/images/main/jeju_book1.jpg' },
    { title: '심리학의 이해', thumbUrl: '/images/main/jeju_book2.jpg' },
    { title: '치료적현존', thumbUrl: '/images/main/jeju_book3.jpg' },
    { title: '인간심리의 이해', thumbUrl: '/images/main/jeju_book4.jpg' },
    { title: '간호진단의 정의와 분류', thumbUrl: '/images/main/jeju_book5.jpg' },
    { title: '사회복지학개론', thumbUrl: '/images/main/jeju_book6.jpg' },
    { title: '상담심리학', thumbUrl: '/images/main/jeju_book7.jpg' },
    { title: '최신 이상심리학', thumbUrl: '/images/main/jeju_book8.jpg' },
  ],
  seoul: [
    { title: '지역사회 복지론', thumbUrl: '/images/main/seoul_book1.jpg' },
    { title: '심리학의 이해', thumbUrl: '/images/main/seoul_book2.jpg' },
    { title: '심리학의 세계', thumbUrl: '/images/main/seoul_book3.jpg' },
    { title: '쉽게 슨 자기심리학', thumbUrl: '/images/main/seoul_book4.jpg' },
    { title: '현대 심리치료와 상담이론', thumbUrl: '/images/main/seoul_book5.jpg' },
    { title: '사회복지학개론', thumbUrl: '/images/main/seoul_book6.jpg' },
    { title: '인간심리의 이해', thumbUrl: '/images/main/seoul_book7.jpg' },
    { title: '간호진단 정의와 분류', thumbUrl: '/images/main/seoul_book8.jpg' },
  ],
  kyunghee: [
    { title: '최신 이상심리학', thumbUrl: '/images/main/kyunghee_book1.jpg' },
    { title: '인간심리의 이해', thumbUrl: '/images/main/kyunghee_book2.jpg' },
    { title: '간호진단 정의와 분류', thumbUrl: '/images/main/kyunghee_book3.jpg' },
    { title: '사회정서발달', thumbUrl: '/images/main/kyunghee_book4.jpg' },
    { title: '치료적현존', thumbUrl: '/images/main/kyunghee_book5.jpg' },
    { title: '상담심리학', thumbUrl: '/images/main/kyunghee_book6.jpg' },
    { title: '긍정심리학', thumbUrl: '/images/main/kyunghee_book7.jpg' },
    { title: '사회복지학개론', thumbUrl: '/images/main/kyunghee_book8.jpg' },
  ],
  gunkuk: [
    { title: '상담심리학', thumbUrl: '/images/main/gunkuk_book1.jpg' },
    { title: '노인복지론', thumbUrl: '/images/main/gunkuk_book2.jpg' },
    { title: '인지행동치료', thumbUrl: '/images/main/gunkuk_book3.jpg' },
    { title: '상담이론과 실제', thumbUrl: '/images/main/gunkuk_book4.jpg' },
    { title: '현대 심리치료와 상담이론', thumbUrl: '/images/main/gunkuk_book5.jpg' },
    { title: '정서행동장애', thumbUrl: '/images/main/gunkuk_book6.jpg' },
    { title: '발달심리학', thumbUrl: '/images/main/gunkuk_book7.jpg' },
    { title: '쉽게 쓴 자기심리학', thumbUrl: '/images/main/gunkuk_book8.jpg' },
  ],
};

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { isAuthenticated } = useFrontAuthStore();
  const [school, setSchool] = useState<string>('');
  const [department, setDepartment] = useState<string>('');
  const [universities, setUniversities] = useState<University[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [isLoadingUniversities, setIsLoadingUniversities] = useState(false);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(false);
  const [selectedUniversity, setSelectedUniversity] = useState<string>('jeju');
  const [openFAQIndex, setOpenFAQIndex] = useState<number | null>(0);
  const [isFamilySiteOpen, setIsFamilySiteOpen] = useState(false);
  const familySiteRef = React.useRef<HTMLDivElement>(null);

  const sectionRef = useRef<HTMLElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const carouselInnerRef = useRef<HTMLDivElement>(null);
  const universityRefs = useRef<(HTMLDivElement | null)[]>([]);
  const scrollTriggerRef = useRef<globalThis.ScrollTrigger | null>(null);
  const snapPositionsRef = useRef<number[]>([]);
  const activeUniversityRef = useRef<string>('jeju');
  const isProgrammaticScrollRef = useRef(false); 
  const featuresSectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (familySiteRef.current && !familySiteRef.current.contains(e.target as Node)) {
        setIsFamilySiteOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 학교 목록 조회
  useEffect(() => {
    const fetchUniversities = async () => {
      setIsLoadingUniversities(true);
      try {
        const data = await getUniversities();
        setUniversities(data);
      } catch (error) {
        // console.error('학교 목록 조회 실패:', error);
      } finally {
        setIsLoadingUniversities(false);
      }
    };

    fetchUniversities();
  }, []);

  // 학교 선택 시 학과 목록 조회
  useEffect(() => {
    const fetchDepartments = async () => {
      if (!school) {
        setDepartments([]);
        setDepartment('');
        return;
      }

      setIsLoadingDepartments(true);
      try {
        const universityNo = parseInt(school);
        const data = await getDepartments(universityNo);
        setDepartments(data);
        setDepartment(''); // 학교 변경 시 학과 초기화
      } catch (error) {
        // console.error('학과 목록 조회 실패:', error);
        setDepartments([]);
        setDepartment('');
      } finally {
        setIsLoadingDepartments(false);
      }
    };

    fetchDepartments();
  }, [school]);

  const features = [
    {
      title: '교수의 노트 공유',
      description: '교수님이 강조하는 학습 포인트, <br/>따로 적지 않고 실시간으로 공유받으세요',
      thumbnail: '/images/main/feature1.jpg',
    },
    {
      title: 'AI 인터랙티브 북',
      description: '모르는 내용은 AI에게 바로 묻고, <br/>맞춤형 퀴즈로 이해도를 즉시 점검합니다.',
      thumbnail: '/images/main/feature2.jpg',
    },
    {
      title: '올인원 학습 툴킷',
      description: '링크, 녹음, 밑줄, 메모 공부의 흐름이 끊기지 않도록 <br/>필요한 학습 기능은 모두 지원합니다.',
      thumbnail: '/images/main/feature3.jpg',
    },
  ];

  const faqs = [
    {
      question: '이 서비스는 일반적인 전자책(e-book)이나 학습 앱과 무엇이 다른가요?',
      answer: '단순한 뷰어가 아닙니다. 당신의 모든 읽기, 대화, 기록을 학습으로 전환해주는 \'퍼스널 학습 코치\'입니다. 교재에 AI가 결합되어 실시간 문답과 퀴즈가 가능하며, 수업중, 교수님과의 화면 동기화 등 능동적인 학습 환경을 제공합니다.',
    },
    {
      question: 'AI가 어떻게 공부를 도와주나요? 정말 정확한 답변을 얻을 수 있나요?',
      answer: '도서의 내용을 완벽하게 학습한 전담 AI가 탑재되어 있습니다. 모르는 문장은 즉시 해설해주고, 본문 내용을 바탕으로 맞춤형 퀴즈를 생성해 이해도를 점검해줍니다. 필요시 도서 내용에 국한되지 않고 일반 검색도 함께 활용하기에 최적의 정보를 제공합니다.',
    },
    {
      question: '교수님과의 화면 동기화 기능은 어떻게 활용하나요?',
      answer: '수업 중 교수님이 강조하거나 필기하는 내용을 공유 받을 수 있습니다. 중요한 포인트를 놓칠까 봐 필기에만 급급할 필요가 없습니다. 교수님의 시선을 그대로 공유 받으며 강의의 본질에 더 집중할 수 있습니다.',
    },
    {
      question: '녹음, 밑줄, 메모 등 여러 기능을 한 번에 사용하면 관리가 복잡하지 않나요?',
      answer: '기능은 다양하게, 관리는 심플하게 구분 되어집니다. 페이지 단위로 펼쳐지는 당신만의 입체적인 학습 레이어를 경험하세요.',
    },
  ];

  const handleEnterLibrary = () => {
    if (school && department) {
      // 선택한 학교/학과 이름을 localStorage에 저장
      const selectedUniv = universities.find(u => String(u.university_no) === school);
      const selectedDept = departments.find(d => String(d.department_no) === department);
      if (selectedUniv && selectedDept) {
        localStorage.setItem('selected_library', JSON.stringify({
          universityName: selectedUniv.name,
          departmentName: selectedDept.name,
        }));
      }
      // 항상 BookList 페이지로 이동 (로그인 불필요)
      navigate(ALL_ROUTES.BOOK_LIST);
    }
  };

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const inner = carouselInnerRef.current;
      const container = carouselRef.current;
      const section = sectionRef.current;
      if (!inner || !container || !section) return;

      const refs = universityRefs.current.filter(Boolean) as HTMLDivElement[];
      if (refs.length < 2) return;

      const evenCardOffset = 200;
      const maxTranslate = inner.offsetHeight + evenCardOffset - container.offsetHeight;
      if (maxTranslate <= 0) return;

      const snapPositions = refs.map((ref) => {
        const top = ref.offsetTop;
        return Math.min(top / maxTranslate, 1);
      });
      if (snapPositions[snapPositions.length - 1] < 1) {
        snapPositions.push(1);
      }
      snapPositionsRef.current = snapPositions;

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          pin: true,
          pinSpacing: true,
          anticipatePin: 1,
          scrub: 0.6,
          snap: {
            snapTo: (value: number) => {                                                                                                                                                                                             
              if (isProgrammaticScrollRef.current) return value;                                                                                                                                                                     
              return snapPositions.reduce((closest, pos) =>                                                                                                                                                                          
                Math.abs(pos - value) < Math.abs(closest - value) ? pos : closest                                                                                                                                                    
              );                                                                                                                                                                                                                     
            },    
            duration: { min: 0.3, max: 0.6 },
            delay: 0.3,
            ease: 'power1.inOut',
          },
          start: 'top top',
          end: `+=${maxTranslate}`,
          onUpdate: (self) => {
            const progress = self.progress;
            let idx = 0;
            for (let i = snapPositions.length - 1; i >= 0; i--) {
              if (progress >= snapPositions[i] - 0.01) {
                idx = Math.min(i, universityTabs.length - 1);
                break;
              }
            }
            const newKey = universityTabs[idx].key;
            if (activeUniversityRef.current !== newKey) {
              activeUniversityRef.current = newKey;
              setSelectedUniversity(newKey);
            }
          },
        },
      });

      scrollTriggerRef.current = tl.scrollTrigger!;

      tl.to(inner, { y: -maxTranslate, ease: 'none' });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const section = featuresSectionRef.current;
      if (!section) return;

      const cards = section.querySelectorAll<HTMLElement>('.feature-card');
      gsap.set(cards, { opacity: 0, y: 80 });

      cards.forEach((card, i) => {
        gsap.to(card, {
          opacity: 1,
          y: 0,
          duration: 1,
          delay: i * 0.2,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 50%',
            toggleActions: 'play none none none',
          },
        });
      });
    }, featuresSectionRef);

    return () => ctx.revert();
  }, []);

  const handleSelectUniversity = (key: string, index: number) => {
    const st = scrollTriggerRef.current;
    if (!st) return;

    activeUniversityRef.current = key; 
    setSelectedUniversity(key); 

    const progress = snapPositionsRef.current[index] ?? 0;
    const targetScroll = st.start + (st.end - st.start) * progress;

    isProgrammaticScrollRef.current = true;
    gsap.killTweensOf(window); 

    gsap.to(window, {
      scrollTo: { y: targetScroll, autoKill: false },
      duration: 0.6,
      ease: 'power2.inOut',
      onComplete: () => {                                                                                                                                                                                                            
        isProgrammaticScrollRef.current = false;                                                                                                                                                                                     
      },
    });
  };

  const toggleFAQ = (index: number) => {
    setOpenFAQIndex(openFAQIndex === index ? null : index);
  };

  return (
    <PageContainer>
      <IntroSection>
        <Header>
          <Logo onClick={() => navigate('/')}><img src="/images/global/logo.svg" alt="logo" /></Logo>
          <HeaderButtons>
            <HeaderButton onClick={() => navigate(ALL_ROUTES.REGISTER_TERMS)}>
              회원가입
            </HeaderButton>
            <HeaderButton onClick={() => navigate(ALL_ROUTES.LOGIN)}>
              로그인
            </HeaderButton>
            <DarkModeToggle isDark={isDarkMode} onToggle={toggleDarkMode} />
          </HeaderButtons>
        </Header>

        <HeroSection>
          <HeroTitle>한 차원 높은 <strong>지능형 학습 파트너</strong><br />성장을 그리는 스마트 전자책</HeroTitle>
          <HeroSubtitle>내가 읽고, 듣고, 쓰고, 대화한 내용이 곧 학습 도우미가 되다</HeroSubtitle>
          
          <InputGroup>
            <SelectWrapper>
              <SearchableSelect
                options={universities}
                value={school}
                onChange={setSchool}
                placeholder="소속된 학교를 선택해주세요."
                disabled={isLoadingUniversities}
                getOptionValue={(univ) => String(univ.university_no)}
                getOptionLabel={(univ) => univ.name}
              />
            </SelectWrapper>
            <SelectWrapper>
              <SearchableSelect
                options={departments}
                value={department}
                onChange={setDepartment}
                placeholder="학과를 선택해주세요."
                disabled={!school || isLoadingDepartments}
                getOptionValue={(dept) => String(dept.department_no)}
                getOptionLabel={(dept) => dept.name}
              />
            </SelectWrapper>
            <EnterButton onClick={handleEnterLibrary}>
              서재 입장
            </EnterButton>
          </InputGroup>

          <PersonalLearnerLink to={ALL_ROUTES.LOGIN}>
            소속 회원이 아닌 개인 학습자는 이곳을 클릭해주세요 →
          </PersonalLearnerLink>
        </HeroSection>
      </IntroSection>
      <MessageSection>
        <MarqueeTrack>
          {Array.from({ length: 10 }).map((_, i) => (
            <MessageTitle key={i}>+++ 내가 읽고, 듣고, 쓰고, 대화하며 나만의 완전한 학습을 완성하다.</MessageTitle>
          ))}
        </MarqueeTrack>
      </MessageSection>

      <BookCarouselSection ref={sectionRef}>
        <BookHeader>
          <BookHeaderTitle>
            <img src="/images/main/icon_header_arrow.svg" alt="curriculum" />
            <strong>Curriculum</strong>
          </BookHeaderTitle>
          <BookHeaderDescription>앞서가는 학습자의 선택,<br/> 주요 대학 커리큘럼 한눈에 보기</BookHeaderDescription>
          <UniversitySelect>
            {universityTabs.map((tab, tabIndex) => (
              <button
                key={tab.key}
                type="button"
                className={selectedUniversity === tab.key ? 'active' : ''}
                onClick={() => handleSelectUniversity(tab.key, tabIndex)}
              >
                {tab.label}
              </button>
            ))}
          </UniversitySelect>
        </BookHeader>
        
        <CarouselContainer ref={carouselRef}>
          <CarouselInner ref={carouselInnerRef}>
            {universityTabs.flatMap((tab, tabIndex) =>
              booksByUniversity[tab.key].map((book, index) => (
                <BookCard
                  key={`${tab.key}-${index}`}
                  ref={index === 0 ? (el: HTMLDivElement | null) => { universityRefs.current[tabIndex] = el; } : undefined}
                >
                  <BookTitle>
                    <img src="/images/global/icon_book_arrow.svg" alt="download_arrow" />
                    <span>{book.title}</span>
                  </BookTitle>
                  <BookCover><img src={book.thumbUrl} alt="book_cover" /></BookCover>
                </BookCard>
              ))
            )}
          </CarouselInner>
        </CarouselContainer>
      </BookCarouselSection>

      <MessageSection>
        <MarqueeTrack>
          {Array.from({ length: 10 }).map((_, i) => (
            <MessageTitle key={i}>+++ 내가 읽고, 듣고, 쓰고, 대화하며 나만의 완전한 학습을 완성하다.</MessageTitle>
          ))}
        </MarqueeTrack>
      </MessageSection>

      <FeaturesSection ref={featuresSectionRef}>
        <TemplateHeader>
          <TemplateSubTitle>
            <img src="/images/main/icon_header_arrow.svg" alt="Feature" />
            <strong>Feature</strong>
          </TemplateSubTitle>
          <TemplateTitle>읽고, 묻고, 기록하는 모든 학습이 <br/>한 권으로 완성됩니다.</TemplateTitle>
        </TemplateHeader>

        <FeaturesGrid>
          {features.map((feature, index) => (
            <FeatureCard key={index} className="feature-card">
              <FeatureCover><img src={feature.thumbnail} alt="feature" /></FeatureCover>
              <FeatureContent>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription dangerouslySetInnerHTML={{ __html: feature.description }} />
              </FeatureContent>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </FeaturesSection>

      <PriceSection>
        <TemplateHeader>
          <TemplateSubTitle>
            <img src="/images/main/icon_header_arrow.svg" alt="Feature" />
            <strong>TAILORED PRICING</strong>
          </TemplateSubTitle>
          <TemplateTitle>읽고, 묻고, 기록하는 모든 학습이 <br/>한 권으로 완성됩니다.</TemplateTitle>
        </TemplateHeader>

        <PriceContent>
          <h4>B2B 학과 라이선스</h4>
          <PriceList>
            <PriceItem>단일 학과 (예: 간호학과)</PriceItem>
            <PriceItem>학기당 500만원</PriceItem>
            <PriceItem>학과 정원 내 (약 200명)</PriceItem>
            <PriceItem>해당 학과 전공 서적</PriceItem>
          </PriceList>
        </PriceContent>
      </PriceSection>

      <FAQSection>
        <TemplateHeader>
          <TemplateSubTitle>
            <img src="/images/main/icon_header_arrow.svg" alt="FAQ" />
            <strong>FAQ</strong>
          </TemplateSubTitle>
          <TemplateTitle>자주 궁금해 하는 질문들</TemplateTitle>
        </TemplateHeader>

        <FAQList>
          {faqs.map((faq, index) => (
            <FAQItem key={index}>
              <FAQQuestion $isOpen={openFAQIndex === index} onClick={() => toggleFAQ(index)}>
                {faq.question}
                <FAQIcon $isOpen={openFAQIndex === index}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M0 6.5H14V7.5H0V6.5Z" fill="#fff"/>
                    <path d="M7.5 4.37121e-08L7.5 14H6.5L6.5 0L7.5 4.37121e-08Z" fill="#fff"/>
                  </svg>
                </FAQIcon>
              </FAQQuestion>
              <FAQAnswer $isOpen={openFAQIndex === index}>{faq.answer}</FAQAnswer>
            </FAQItem>
          ))}
        </FAQList>
      </FAQSection>

      <Footer>
        <FooterTitle>
          지식은 무겁지 않아요. 언제든 가볍게 펼치고,<br/>
          내가 원하는 만큼 깊이 배울 수 있어요.
        </FooterTitle>
        <FooterContent>
          <FooterHeader>
            <h2><a href="/"><img src="/images/global/logo.svg" alt="logo" /></a></h2>
            <FamilySiteWrapper ref={familySiteRef}>
              <FamilySiteButton
                $isOpen={isFamilySiteOpen}
                onClick={() => setIsFamilySiteOpen(prev => !prev)}
              >
                Family Site
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1L5 5L9 1" stroke="#4b4b4b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </FamilySiteButton>
              <FamilySiteMenu $isOpen={isFamilySiteOpen}>
                <li><a href="https://branding.hakjisa.kr" target="_blank" rel="noreferrer">HCG</a></li>
                <li><a href="https://www.inpsyt.co.kr/" target="_blank" rel="noreferrer">INPSYT</a></li>
                <li><a href="https://www.hakjisa.co.kr/" target="_blank" rel="noreferrer">학지사</a></li>
              </FamilySiteMenu>
            </FamilySiteWrapper>
          </FooterHeader>

          <FooterInfo>
            <FooterInfoItem>
              <strong>(주)학지사</strong>
              <span>대표 김진환</span>
              <span>Fax : 02-324-2345</span>
              <span>사업자등록번호 105-87-03735 (사업자정보 확인)</span>
              <span>통신사업자 신고번호 : 2008-서울마포-0666</span>
              <span>서울특별시 마포구 양화로 15길 20 마인드월드빌딩 5층</span>
            </FooterInfoItem>
            <dl>
              <dt>고객센터</dt>
              <dd>
                {/* <p><strong>E-mail</strong><a href="psyt@inpsyt.co.kr">psyt@inpsyt.co.kr</a></p> */}
                <p><strong>Tel</strong><span>02-330-5114 </span></p>
              </dd>
            </dl>
          </FooterInfo>

          <FooterCopyright>
            <address>&copy; Copyright 2026 HAKJISA. ALL RIGHTS RESERVED.</address>
            <FooterNav>
              <li><a href="/">서비스소개</a></li>
              <li><a href="/">이용약관</a></li>
              <li><a href="/">개인정보처리방침</a></li>
              <li><a href="/">1:1 문의하기</a></li>
            </FooterNav>
          </FooterCopyright>
          
        </FooterContent>
      </Footer>
    </PageContainer>
  );
};

export default LandingPage;
